// SpecOutcomeCard — read-only display of the SpecExecutionOutcome record.
// Renders nothing until the auto-derive effect produces an outcome (i.e. all
// phases QA-passed). No actions, no editing — outcomes are facts.

import { useMemo } from "react";
import { Pin, AlertTriangle, ShieldCheck, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import {
  formatSpecRef,
  getLatestCanonicalVersion,
  isLegacyBinding,
  resolveBoundSpec,
  type BoundJobSpec,
} from "@/data/jobSpec";
import {
  getOutcome,
  totalPhaseDuration,
  qaFirstPass,
} from "@/data/specExecutionOutcome";

interface Props {
  jobId: string;
  bound?: BoundJobSpec;
}

function fmtDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function reconciliationLabel(d?: "spec_valid" | "deviation_required"): string {
  if (!d) return "—";
  return d === "spec_valid" ? "Spec Valid" : "Deviation Logged";
}

function kitLabel(s: "fully_confirmed" | "override" | "legacy_no_kit"): string {
  if (s === "fully_confirmed") return "Fully Confirmed";
  if (s === "override") return "Override Used";
  return "Legacy (no kit)";
}

export function SpecOutcomeCard({ jobId, bound }: Props) {
  const { specOutcomes } = useAppData();
  const outcome = useMemo(() => getOutcome(specOutcomes, jobId), [specOutcomes, jobId]);
  const resolvedSpec = useMemo(() => (bound ? resolveBoundSpec(bound) : null), [bound]);

  if (!outcome || !bound) return null;

  const latest = getLatestCanonicalVersion(bound.specId);
  const total = totalPhaseDuration(outcome);
  const firstPass = qaFirstPass(outcome);

  return (
    <Card className="shadow-none">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Spec Outcome · v{outcome.outcomeVersion}
            </p>
            <h3 className="text-base font-semibold leading-tight mt-0.5">
              {formatSpecRef(bound)}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              Completed {new Date(outcome.completedAt).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="text-[10px] gap-1">
              <Pin className="h-2.5 w-2.5" />
              Pinned to v{outcome.specVersion}
            </Badge>
            {latest !== undefined && latest > outcome.specVersion && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 border-amber-500/40 text-amber-600 dark:text-amber-400"
              >
                Latest is v{latest}
              </Badge>
            )}
            {(outcome.isLegacyBinding || isLegacyBinding(bound)) && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 border-destructive/40 text-destructive"
              >
                <AlertTriangle className="h-2.5 w-2.5" />
                Legacy binding
              </Badge>
            )}
          </div>
        </div>

        {/* Key facts grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Fact
            label="QA First-Pass"
            value={firstPass ? "Yes" : "No"}
            tone={firstPass ? "ok" : "warn"}
          />
          <Fact
            label="QA Failures"
            value={String(outcome.qaFailureCount)}
            tone={outcome.qaFailureCount === 0 ? "ok" : "warn"}
          />
          <Fact
            label="Reconciliation"
            value={reconciliationLabel(outcome.reconciliationDecision)}
            tone={
              outcome.reconciliationDecision === "deviation_required" ? "warn" : "neutral"
            }
          />
          <Fact
            label="Material Kit"
            value={kitLabel(outcome.materialKitStatus)}
            tone={outcome.materialOverrideUsed ? "warn" : "neutral"}
          />
        </div>

        {/* Deviation reason callout */}
        {outcome.reconciliationDecision === "deviation_required" && outcome.deviationReason && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
            <span className="font-semibold">Deviation reason:</span>{" "}
            <code className="font-mono">{outcome.deviationReason}</code>
          </div>
        )}

        {/* Material override reason callout */}
        {outcome.materialOverrideUsed && outcome.materialOverrideReason && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
            <span className="font-semibold">Override reason:</span>{" "}
            <code className="font-mono">{outcome.materialOverrideReason}</code>
          </div>
        )}

        {/* Phase durations summary */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            Phase Durations (submit → QA pass) · Total {fmtDuration(total)}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            {(resolvedSpec?.phases?.map((p) => p.name) ?? Object.keys(outcome.phaseDurations)).filter(
              (n) => n in outcome.phaseDurations,
            ).map((name) => {
              const d = outcome.phaseDurations[name] ?? 0;
              const fp = outcome.phaseQaFirstPass[name];
              const rework = outcome.reworkLoopsByPhase[name] ?? 0;
              return (
                <div
                  key={name}
                  className="rounded-md border px-2 py-1.5 flex flex-col gap-0.5"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wider">
                      {name}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-[9px] ${fp ? "" : "border-amber-500/40 text-amber-600 dark:text-amber-400"}`}
                    >
                      {fp ? "1st-pass" : `${rework} rework`}
                    </Badge>
                  </div>
                  <span className="text-xs font-mono">{fmtDuration(d)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {outcome.contractorId && (
          <p className="text-[10px] text-muted-foreground">
            Contractor: <span className="font-mono">{outcome.contractorId}</span>
            {outcome.payProfileId && (
              <>
                {" · "}Pay Profile:{" "}
                <span className="font-mono">{outcome.payProfileId}</span>
              </>
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Fact({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "neutral";
}) {
  const toneClass =
    tone === "ok"
      ? "text-success"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : "text-foreground";
  return (
    <div className="rounded-md border p-2">
      <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`text-sm font-semibold mt-0.5 ${toneClass}`}>{value}</p>
    </div>
  );
}

export default SpecOutcomeCard;
